/**
 * QuestionVisibilityControls
 *
 * Sub-component of SectionQuestions. Owns the per-question conditional visibility
 * UI ("Only show when [Question] is [Answer]") for V2 sectioned surveys.
 *
 * Mirrors the V1 ConditionalVisibilityControls UX 1:1:
 *   - Checkbox + "Only show when" + Question Select + "is" + first Answer Select
 *   - Additional answer rows with "-or-" label, X-remove, and `+ Add Answer`
 *
 * Backs the V2 RHF `visibleWhen` field directly (no parent Map indirection):
 *   - 0 answers          → visibleWhen = undefined
 *   - 1 answer           → visibleWhen = SimpleEqualityQuestionVisibility
 *   - 2+ answers (OR'd)  → visibleWhen = LogicalQuestionVisibility { operator: 'Or' }
 *
 * A Linear Scale / NPS source swaps the answer picker for an operator + threshold pair
 * ("Only show when NPS score is less than or equal to 6"), writing a
 * ComparisonQuestionVisibility instead.
 *
 * Eligible source questions are DropDown / Checkbox / Linear Scale / NPS questions from
 * prior sections AND earlier questions in the current section. Renders nothing if no
 * eligible sources exist (matches V1 guard).
 *
 * Unlike V2's SectionTriggers, this control does NOT auto-mark the source question
 * as required — matches V1 behavior for question-level visibility.
 */
import { css } from 'aphrodite';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Button } from '../../../../../../../components-v2/Button';
import { Checkbox } from '../../../../../../../components-v2/Checkbox';
import {
	Combobox,
	ComboboxCollection,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxGroup,
	ComboboxGroupLabel,
	ComboboxInput,
	ComboboxItem,
	ComboboxItemRadioIndicator,
	ComboboxList,
} from '../../../../../../../components-v2/Combobox';
import { XIcon } from '../../../../../../../components/svgs/icons/XIcon';
import { bs } from '../../../../../../../styles/styles';
import { usePreviewMode } from '../../../../contexts/PreviewModeContext';
import type { NumericComparisonOperator } from '@ViewModels';
import type { QuestionVisibility, SurveyPart } from '../../types';
import { ComparisonRow } from './ComparisonRow';
import { styleSheet } from './QuestionVisibilityControls.styles';
import type { Draft, SourceEntry, SourceGroup } from './conditionalVisibilitySources';
import {
	DEFAULT_COMPARISON_OPERATOR,
	buildEligibleSources,
	computeUsedByOtherRows,
	describeSource,
	fromVisibleWhen,
	groupEligibleSources,
	isNumericSource,
	parseThreshold,
	resolveThresholdBounds,
	toVisibleWhen,
} from './conditionalVisibilitySources';

// ---------------------------------------------------------------------------
// Public props
// ---------------------------------------------------------------------------

export interface QuestionVisibilityControlsProps {
	/** RHF field name for the question, e.g. "surveyForm.surveyParts.2.questions.1". */
	questionFieldNamePrefix: string;
	/** Zero-based index of the containing section in allSurveyParts. */
	sectionIndex: number;
	/** Zero-based index of this question within its section. */
	questionIndex: number;
	/** Full ordered list of survey parts (sections + flat questions). */
	allSurveyParts: SurveyPart[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const QuestionVisibilityControls: React.FC<QuestionVisibilityControlsProps> = ({
	questionFieldNamePrefix,
	sectionIndex,
	questionIndex,
	allSurveyParts,
}) => {
	const { setValue } = useFormContext();
	const { isPreviewOnly } = usePreviewMode();

	const visibleWhen = useWatch<Record<string, QuestionVisibility | undefined>>({
		name: `${questionFieldNamePrefix}.visibleWhen`,
	});

	const [draft, setDraft] = React.useState<Draft>(() => fromVisibleWhen(visibleWhen));

	React.useEffect(() => {
		const committed = toVisibleWhen(draft);
		const committedKey = committed ? JSON.stringify(committed) : '';
		const externalKey = visibleWhen ? JSON.stringify(visibleWhen) : '';
		if (committedKey !== externalKey) {
			setDraft(fromVisibleWhen(visibleWhen));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [visibleWhen]);

	const eligibleSources = React.useMemo(
		() => buildEligibleSources(allSurveyParts, sectionIndex, questionIndex),
		[allSurveyParts, sectionIndex, questionIndex]
	);

	// Same eligible sources, grouped by section for the Combobox's grouped `items`.
	const groupedSources = React.useMemo(
		() => groupEligibleSources(eligibleSources, allSurveyParts),
		[eligibleSources, allSurveyParts]
	);

	const selectedSource = React.useMemo(
		() => eligibleSources.find(s => s.question.fieldId === draft.dependsOnFieldId),
		[eligibleSources, draft.dependsOnFieldId]
	);

	const answerOptions = React.useMemo(() => {
		if (!selectedSource || isNumericSource(selectedSource.question)) return [];
		return (selectedSource.question.options ?? [])
			.filter(o => !!o.label?.trim())
			.map(o => ({
				// V1's `ConditionalVisibilityControls` uses `opt.id` as the persisted
				// fieldValue. Each option has a unique `id` while `fieldId` is the
				// shared parent-question id — using `fieldId` here makes every
				// option in the Select indistinguishable. Match V1 by writing `id`.
				value: o.id ?? o.label ?? '',
				label: o.label ?? '',
			}));
	}, [selectedSource]);

	/**
	 * Cap on how many answer rows the rule may have: the number of valid
	 * options. DropDownQuestion and CheckBoxesQuestion sources both allow up to
	 * N answer rows — a rule may reference every option.
	 *
	 * Deps use the PRIMITIVE `_type` string (not the `selectedSource` object
	 * reference). RHF mutates a question's `_type` in place when a sub-path
	 * setValue lands on it, so the source-question object reference can be
	 * stable across renders even though `_type` changed. Reading the primitive
	 * each render guarantees this memo recomputes when the type flips.
	 */
	const sourceType = selectedSource?.question?._type;
	const maxAnswerRows = React.useMemo(() => {
		if (!sourceType) return 0;
		return answerOptions.length;
	}, [sourceType, answerOptions.length]);

	const commit = React.useCallback(
		(next: Draft) => {
			setDraft(next);
			setValue(`${questionFieldNamePrefix}.visibleWhen`, toVisibleWhen(next), { shouldDirty: true });
		},
		[questionFieldNamePrefix, setValue]
	);

	const handleToggle = React.useCallback(
		(checked: boolean | 'indeterminate') => {
			const enabled = Boolean(checked);
			if (!enabled) {
				commit(fromVisibleWhen(undefined));
				return;
			}
			setDraft(prev => ({ ...prev, enabled: true }));
		},
		[commit]
	);

	const handleQuestionChange = React.useCallback(
		(sourceFieldId: string) => {
			const entry = eligibleSources.find(e => e.question.fieldId === sourceFieldId);
			commit({
				enabled: true,
				dependsOnFieldId: sourceFieldId,
				dependsOnOptionIds: [],
				mode: isNumericSource(entry?.question) ? 'comparison' : 'options',
				operator: DEFAULT_COMPARISON_OPERATOR,
				threshold: '',
			});
		},
		[eligibleSources, commit]
	);

	const handleOperatorChange = React.useCallback(
		(operator: NumericComparisonOperator) => commit({ ...draft, operator }),
		[draft, commit]
	);

	const handleThresholdChange = React.useCallback(
		(threshold: string) => {
			// A cleared box is mid-edit, not "delete the rule" — committing would wipe a saved rule.
			if (parseThreshold(threshold) === null) {
				setDraft(prev => ({ ...prev, threshold }));
				return;
			}
			commit({ ...draft, threshold });
		},
		[draft, commit]
	);

	const handleAnswerChange = React.useCallback(
		(answerIndex: number, value: string) => {
			const next = [...draft.dependsOnOptionIds];
			next[answerIndex] = value;
			commit({ ...draft, dependsOnOptionIds: next });
		},
		[draft, commit]
	);

	const handleAnswerAdd = React.useCallback(() => {
		setDraft(prev => ({ ...prev, dependsOnOptionIds: [...prev.dependsOnOptionIds, ''] }));
	}, []);

	const handleAnswerRemove = React.useCallback(
		(answerIndex: number) => {
			const next = draft.dependsOnOptionIds.filter((_, i) => i !== answerIndex);
			commit({ ...draft, dependsOnOptionIds: next });
		},
		[draft, commit]
	);

	if (eligibleSources.length === 0) return null;

	const isReadOnly = isPreviewOnly || !!draft.unsupported;
	const isComparison = draft.mode === 'comparison';
	const thresholdBounds = resolveThresholdBounds(selectedSource?.question);
	const isQuestionSelectDisabled = !draft.enabled || isReadOnly;
	const isAnswerSelectDisabled = !draft.enabled || !draft.dependsOnFieldId || isReadOnly;
	const answerCount = draft.dependsOnOptionIds.length;
	const hasFirstAnswer = answerCount > 0;
	const additionalAnswerIndices = hasFirstAnswer ? draft.dependsOnOptionIds.slice(1).map((_, i) => i + 1) : [];

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.row)}>
				<div className={css(styleSheet.conditionGroup)}>
					<Checkbox
						checked={draft.enabled}
						id={`question-visibility-${sectionIndex}-${questionIndex}-checkbox`}
						onCheckedChange={handleToggle}
						disabled={isPreviewOnly}
					/>
					<span>Only show when</span>
					<div className={css(styleSheet.fill)}>
						{/*
						 * Grouped source-question picker. Item values are the SourceEntry
						 * objects themselves so typeahead filtering + the trigger display run
						 * off the human label (`itemToStringLabel`) rather than the fieldId
						 * UUID. `isItemEqualToValue` matches by fieldId so the controlled
						 * `value` resolves regardless of object identity, and `onValueChange`
						 * funnels the chosen fieldId back through `handleQuestionChange`.
						 */}
						<Combobox
							items={groupedSources}
							value={selectedSource ?? null}
							onValueChange={value => {
								const entry = value as SourceEntry | null;
								if (entry) handleQuestionChange(entry.question.fieldId ?? '');
							}}
							itemToStringLabel={value => describeSource(value as SourceEntry)}
							isItemEqualToValue={(item, value) =>
								(item as SourceEntry)?.question.fieldId === (value as SourceEntry | null)?.question.fieldId
							}
							disabled={isQuestionSelectDisabled}
						>
							<ComboboxInput
								placeholder='Select question'
								disabled={isQuestionSelectDisabled}
								aria-label='Select source question'
								styles={[styleSheet.sourceComboboxInput]}
							/>
							<ComboboxContent>
								<ComboboxEmpty>No questions found</ComboboxEmpty>
								<ComboboxList>
									{(group: SourceGroup) => (
										<ComboboxGroup key={group.value} items={group.items}>
											<ComboboxGroupLabel>{group.value}</ComboboxGroupLabel>
											<ComboboxCollection>
												{(entry: SourceEntry) => (
													<ComboboxItem key={entry.question.fieldId} value={entry}>
														<ComboboxItemRadioIndicator />
														<span className={css(bs.truncateText, styleSheet.comboboxItemLabel)}>
															{describeSource(entry)}
														</span>
													</ComboboxItem>
												)}
											</ComboboxCollection>
										</ComboboxGroup>
									)}
								</ComboboxList>
							</ComboboxContent>
						</Combobox>
					</div>
					<span>is</span>
				</div>
				<div className={css(styleSheet.answerColumn)}>
					{isComparison ? (
						<ComparisonRow
							operator={draft.operator}
							threshold={draft.threshold}
							bounds={thresholdBounds}
							disabled={isAnswerSelectDisabled}
							onOperatorChange={handleOperatorChange}
							onThresholdChange={handleThresholdChange}
						/>
					) : (
						<>
							<AnswerRow
								answerOptions={answerOptions}
								selectedValue={hasFirstAnswer ? draft.dependsOnOptionIds[0] : ''}
								usedByOtherRows={computeUsedByOtherRows(draft.dependsOnOptionIds, 0)}
								answerIndex={0}
								disabled={isAnswerSelectDisabled}
								isPreviewOnly={isPreviewOnly}
								onAnswerChange={handleAnswerChange}
							/>
							{additionalAnswerIndices.map(answerIdx => (
								<div key={`or-row-${answerIdx}`} className={css(styleSheet.orRow)}>
									<span className={css(styleSheet.orLabel)}>-or-</span>
									<AnswerRow
										answerOptions={answerOptions}
										selectedValue={draft.dependsOnOptionIds[answerIdx] ?? ''}
										usedByOtherRows={computeUsedByOtherRows(draft.dependsOnOptionIds, answerIdx)}
										answerIndex={answerIdx}
										disabled={isAnswerSelectDisabled}
										isPreviewOnly={isPreviewOnly}
										onAnswerChange={handleAnswerChange}
										showRemove
										onRemove={() => handleAnswerRemove(answerIdx)}
									/>
								</div>
							))}
						</>
					)}
					{/*
					 * "+ Add Answer" hides when the rule has reached the cap: both
					 * DropDownQuestion and CheckBoxesQuestion sources allow up to N answer
					 * rows (one per option). Always hidden in preview mode.
					 */}
					{!isComparison && draft.enabled && !!draft.dependsOnFieldId && !isReadOnly && answerCount < maxAnswerRows && (
						<Button type='button' variant='link' onClick={handleAnswerAdd} styles={[styleSheet.addAnswerButton]}>
							+ Add Answer
						</Button>
					)}
				</div>
			</div>
		</div>
	);
};

QuestionVisibilityControls.displayName = 'QuestionVisibilityControls';

// ---------------------------------------------------------------------------
// AnswerRow sub-component
// ---------------------------------------------------------------------------

interface AnswerRowProps {
	answerOptions: { value: string; label: string }[];
	selectedValue: string;
	/**
	 * Option ids already picked by OTHER rows on the same rule. These are
	 * rendered disabled in this row's dropdown so the user can't pick the
	 * same answer twice. The row's own selection stays enabled.
	 */
	usedByOtherRows: Set<string>;
	answerIndex: number;
	disabled: boolean;
	isPreviewOnly: boolean;
	onAnswerChange: (answerIndex: number, value: string) => void;
	showRemove?: boolean;
	onRemove?: () => void;
}

const AnswerRow: React.FC<AnswerRowProps> = React.memo(
	({
		answerOptions,
		selectedValue,
		usedByOtherRows,
		answerIndex,
		disabled,
		isPreviewOnly,
		onAnswerChange,
		showRemove,
		onRemove,
	}) => {
		const handleChange = React.useCallback(
			(value: string) => onAnswerChange(answerIndex, value),
			[answerIndex, onAnswerChange]
		);

		// Augment options with their per-row disabled state so it's data-visible to
		// both Base UI (item disabling) and the controlled value lookup. `value` is
		// the option object itself so typeahead + the trigger display run off the
		// label (`itemToStringLabel`) rather than the raw option id.
		const items = React.useMemo(
			() => answerOptions.map(o => ({ ...o, disabled: usedByOtherRows.has(o.value) })),
			[answerOptions, usedByOtherRows]
		);

		const selectedItem = React.useMemo(
			() => items.find(o => o.value === selectedValue) ?? null,
			[items, selectedValue]
		);

		return (
			<div className={css(styleSheet.answerRow)}>
				<div className={css(styleSheet.fill)}>
					<Combobox
						items={items}
						value={selectedItem}
						onValueChange={value => {
							const opt = value as { value: string } | null;
							if (opt) handleChange(opt.value);
						}}
						itemToStringLabel={value => (value as { label: string }).label}
						isItemEqualToValue={(item, value) =>
							(item as { value: string })?.value === (value as { value: string } | null)?.value
						}
						disabled={disabled}
					>
						<ComboboxInput
							placeholder='Select answer'
							aria-label='Select trigger value'
							disabled={disabled}
							styles={[styleSheet.sourceComboboxInput]}
						/>
						<ComboboxContent>
							<ComboboxEmpty>No values found</ComboboxEmpty>
							<ComboboxList>
								{(opt: { value: string; label: string; disabled: boolean }) => (
									<ComboboxItem key={opt.value} value={opt} disabled={opt.disabled}>
										<ComboboxItemRadioIndicator />
										<span className={css(bs.truncateText, styleSheet.comboboxItemLabel)}>{opt.label}</span>
									</ComboboxItem>
								)}
							</ComboboxList>
						</ComboboxContent>
					</Combobox>
				</div>
				{showRemove && !isPreviewOnly && (
					<Button
						type='button'
						variant='ghost'
						size='icon'
						onClick={onRemove}
						aria-label={`Remove answer ${answerIndex + 1}`}
						title='Remove this answer'
						styles={[styleSheet.answerRemoveButton]}
					>
						<XIcon width={16} height={16} />
					</Button>
				)}
			</div>
		);
	}
);

AnswerRow.displayName = 'AnswerRow';
